import {put, call, all} from 'redux-saga/effects';
import Contact from "../../entities/contact";
import * as Actions from "../actions/contactActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ContactAction = {
    contactId: string;
    contact: Contact;
    contacts: Contact[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneContact(action: ContactAction) {
	const session = tabSession.captureGeneration();
	try {
		const contact: Contact = yield call(api.findOne, 'contact', action.contactId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CONTACT_FETCH_ONE_SUCCESS, contactId: action.contactId, contact, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CONTACT_FETCH_ONE_FAILURE, contactId: action.contactId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ContactAction} action
 */
export function* saveContact(action: ContactAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CONTACT_SAVE saga', action.contact?.id);

		if (action.contact === undefined || action.contact === null) {
			console.error('saveContact received a null');
			throw new Error('saveContact received a null');
		}

		if (!(action.contact instanceof Contact)) {
			action.contact = new Contact(action.contact);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.contact.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.contact);
			const addResult = yield call(idb.add, 'contact', action.contact);
			if (addResult.isErr()) {
				console.error(`⮂ saveContact: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertContactSave(action.contact, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.CONTACT_SAVE_FAILURE, contact: action.contact, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.CONTACT_SAVE, contact: action.contact, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertContactSave(action.contact, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.CONTACT_SAVE_FAILURE, contact: action.contact, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ContactAction} action
 * @param {Contact[]} action.contacts
 */
export function* saveManyContacts(action: ContactAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CONTACT_SAVE_MANY saga', action.contacts.map(contact => contact.id).join(','));

        if (!Array.isArray(action.contacts)) {
            console.error('saveManyContacts expected an array, received', action.contacts);
            throw new Error('saveManyContacts expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveContact), under these queue keys
		const dispatchedAddedOns: number[] = action.contacts.map(contact => contact?.addedOn);
		const revertAll = () => action.contacts.map((contact, i) =>
			put(Actions.revertContactSave(contact, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Contact[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.contacts.length; i++) {
				const contact = action.contacts[i];
				contact.persisted = false;
				ensureOperationId(contact);
				toSave.push(contact);
			}

			const addResult = yield call(idb.add, 'contact', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyContacts: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.CONTACT_SAVE_MANY_FAILURE, contacts: action.contacts, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.CONTACT_SAVE_MANY, contacts: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.CONTACT_SAVE_MANY_FAILURE, contacts: action.contacts, status: 500, err});

			console.error(`Error saving ${action.contacts.length} contacts to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ContactAction} action
 */
export function* removeContact(action: ContactAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.contact === undefined || action.contact === null || !(action.contact instanceof Contact)) {
			console.error('removeContact expected type Contact, received', action.contact);
			reportQueued(false);
			throw new Error('removeContact expected type Contact');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.contact.status = 'D';
		action.contact.addedOn = newHRTimestamp();
		action.contact.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.contact);
		const addResult = yield call(idb.add, 'contact', action.contact);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeContact: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.CONTACT_REMOVE_FAILURE, contact: action.contact, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONTACT_REMOVE, contact: action.contact});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CONTACT_REMOVE_FAILURE, contact: action.contact, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Contact.
	*
	* @param {ContactAction} action
	*/
export function* restoreContact(action: ContactAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreContact expected getCurrentUserId() to return a value');
        return;
    }

	if (action.contact === undefined || action.contact === null) {
        console.error('restoreContact expected a Contact, received null');
        throw new Error('restoreContact expected a Contact, received null');
    }
    else if (!(action.contact instanceof Contact)) action.contact = new Contact(action.contact); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.contact.status = 'U';
		action.contact.addedOn = newHRTimestamp();
		action.contact.addedBy = userId;
		action.contact.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.contact);
		const addResult = yield call(idb.add, 'contact', action.contact);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreContact: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONTACT_RESTORE_FAILURE, contact: action.contact, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONTACT_RESTORE, contact: action.contact});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CONTACT_RESTORE_FAILURE, contact: action.contact, status: 500, err});

		console.error('Error restoring from database', err);
	}
}